import React, { useState, useEffect } from 'react';
import { Data, QUERY_PARAMS } from './types';
import { Link, RouteComponentProps, useHistory } from 'react-router-dom';
import { Layouts, PageLayoutParams } from 'layouts/LayoutProvider';
import Header from './components/Header/Header';
import Table from './components/Table/Table';
import { Button, Icon } from 'lmc-ui-library';
import { registerSearchFirms } from 'routes/routePaths';
import { Select, TextInput } from 'form/components';
import { mapResponseData } from './mapper/mapper';
import { dueDiligenceFilterOptions, firmsTableProperties } from './utils';
import { getFirmsData } from './api';
import styled from 'styled-components';
import Pagination from 'components/Pagination/Pagination';
import { useQuery } from 'components/Pagination/hooks/useQuery';

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Firms: React.FC<RouteComponentProps> & PageLayoutParams = () => {
  const [firms, setFirms] = useState<Data[]>([]);
  const [filterOption, setFilterOption] = useState('all');
  const [filterOptions, setFilterOptions] = useState(dueDiligenceFilterOptions);
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(100);
  const history = useHistory();
  const query = useQuery();

  const getFirms = async (queryParams: Record<string, any>, filter?: string) => {
    try {
      const data = await getFirmsData(queryParams, filter);
      setFirms(mapResponseData(data.results));
      setItemsCount(data.count);
      if (data.page_size) {
        setPageSize(data.page_size);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const filterByDDStatus = (value: string) => {
    if (!value.trim()) {
      setFilterOptions(dueDiligenceFilterOptions);
    } else {
      const filtered = dueDiligenceFilterOptions.filter((filterOption) =>
        filterOption.label.toLowerCase().includes(value.toLowerCase()),
      );
      setFilterOptions(filtered);
    }
  };

  const searchFirms = async (keyword: string) => {
    query.set(QUERY_PARAMS.SEARCH, keyword.toString());
    const queryParams = Object.fromEntries(query.entries());

    if (!queryParams[QUERY_PARAMS.SEARCH]) {
      delete queryParams[QUERY_PARAMS.SEARCH];
    }

    getFirms(Object.assign(queryParams, { page: '1' }), queryParams[QUERY_PARAMS.FILTER]);

    history.push({
      search: `?${new URLSearchParams(queryParams).toString()}`,
    });
  };

  const handlePageChange = (page: number) => {
    const queryParams = Object.fromEntries(query.entries());
    getFirms(Object.assign(queryParams, { page: page.toString() }), filterOption.toString());
    history.push({
      search: `?${new URLSearchParams(queryParams).toString()}`,
    });
  };

  const handleOptionClick = (filter: string) => {
    query.set(QUERY_PARAMS.FILTER, filter);
    const queryParams = Object.fromEntries(query.entries());
    setFilterOption(filter);

    getFirms(Object.assign(queryParams, { page: '1' }), filter);

    history.push({
      search: `?${new URLSearchParams(queryParams).toString()}`,
    });
  };

  useEffect(() => {
    const queryParams = Object.fromEntries(query.entries());
    if (!queryParams[QUERY_PARAMS.FILTER] && !queryParams[QUERY_PARAMS.SEARCH] && !queryParams[QUERY_PARAMS.PAGE]) {
      getFirms(Object.assign(queryParams, { page: '1' }));
    } else {
      getFirms(queryParams, queryParams[QUERY_PARAMS.FILTER]);

      if (queryParams[QUERY_PARAMS.FILTER]) {
        setFilterOption(queryParams[QUERY_PARAMS.FILTER]);
      }
    }
  }, [query]);

  return (
    <>
      <Header />
      <Table
        properties={firmsTableProperties}
        data={firms}
        action={{
          display: (item: Data) => <Link to={`/bp/firm/${item.id}`}>Edit</Link>,
        }}
        emptyState={
          <>
            <p style={{ fontWeight: 'bold' }}>No firms found.</p>
            <p>This may be an error, please try again</p>
          </>
        }
        addButton={
          <Link to={registerSearchFirms}>
            <Button mode="primary" type="button" borderVariant="rectangle" small={true}>
              Add new
            </Button>
          </Link>
        }
        filter={
          <Select
            value={filterOption}
            options={filterOptions.map((ddOption) => ({
              label: ddOption.label.toString(),
              name: ddOption.name.toString(),
              selected: false,
            }))}
            onOptionClick={(option) => {
              if (option.name) {
                handleOptionClick(option.name);
              }
            }}
            onSearchInputChange={filterByDDStatus}
            searchable={false}
            rightIcon={(stateColor, setFocused, isFocused) => (
              <IconWrapper onClick={() => (isFocused ? setFocused(false) : setFocused(true))}>
                <Icon type={isFocused ? 'iconUp' : 'iconDown'} color={'#2d3540'} />
              </IconWrapper>
            )}
          />
        }
        search={
          <TextInput
            value={Object.fromEntries(query.entries())[QUERY_PARAMS.SEARCH]}
            onChange={(e) => searchFirms(e.target.value.trim())}
            placeholder="Search..."
          />
        }
      />

      <Pagination totalCount={itemsCount} pageSize={pageSize} onPageChange={(page: number) => handlePageChange(page)} />
    </>
  );
};

Firms.layout = Layouts.FIRMS;

export default Firms;
